'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { PartList } from 'services/availabilityBySource/types';
import { CartResponse } from 'services/cart/types';

export type productDetailPageContextProp = {
  cartDetails: CartResponse;
  serialNumber: string;
  quantity: number;
  locale: string;
  storeId: string;
  langId: string;
  partsList: PartList[];
  setPartsList: (partsList: PartList[]) => void;
  setCartDetails: (cartDetails: CartResponse) => void;
  setSerialNumber: (serialNumber: string) => void;
  isPCCHeadlessAvailabilityFlag: boolean;
  isCustomerSupportEnabled: boolean;
  helpCenterContactSupportUrl: 'string';
  quantityUpdatedAt: number | null;
  updateQuantity: (quantity: number) => void;
};

export const ProductDetailPageContext =
  createContext<productDetailPageContextProp>({
    cartDetails: null,
    serialNumber: null,
    quantity: 1,
    locale: null,
    storeId: null,
    langId: null,
    partsList: [],
    setPartsList: () => null,
    setCartDetails: () => null,
    setSerialNumber: () => null,
    isPCCHeadlessAvailabilityFlag: false,
    isCustomerSupportEnabled: false,
    helpCenterContactSupportUrl: null,
    quantityUpdatedAt: null,
    updateQuantity: () => null
  });

export const useProductDetailPageContext = () =>
  useContext(ProductDetailPageContext);

export const ProductDetailPageContextProvider = ({ children, value }) => {
  const [quantity, setQuantity] = useState(1);
  const [quantityUpdatedAt, setQuantityUpdatedAt] = useState<number>(null);
  const [partsList, setPartsList] = useState([]);
  const [cartDetails, setCartDetails] = useState(null);
  const [serialNumber, setSerialNumber] = useState(null);
  const updateQuantity = useCallback(
    (newQuantity: number) => {
      if (quantity !== newQuantity) {
        setQuantity(newQuantity);
        setQuantityUpdatedAt(Date.now());
      }
    },
    [quantity]
  );
  const pdpProps = useMemo(() => {
    return {
      ...value,
      quantity,
      cartDetails,
      serialNumber,
      setCartDetails,
      setSerialNumber,
      partsList,
      setPartsList,
      quantityUpdatedAt,
      updateQuantity
    };
  }, [
    value,
    quantity,
    partsList,
    cartDetails,
    serialNumber,
    setCartDetails,
    setSerialNumber,
    quantityUpdatedAt,
    updateQuantity
  ]);

  return (
    <ProductDetailPageContext.Provider value={pdpProps}>
      {children}
    </ProductDetailPageContext.Provider>
  );
};
